import { supabase } from './supabase';
import { API_BASE_URL } from '../config/apiUrl';

/**
 * Account deletion client. Same shape as lib/billingPortal.ts, deliberately:
 * one function, an explicit result union, and no silent failure paths.
 *
 * The request carries NO identifier. The server resolves the caregiver from the
 * JWT — sending an id would be both useless and a security regression.
 */

export interface DeletionReceipt {
  requestId: string | null;
  requestedAt: string;
  shellPurgeAfter: string;
  alreadyCompleted: boolean;
  stripe: {
    subscriptionsCancelled: number;
    refund: string;
    periodEndsAt: string | null;
  };
}

export type DeleteAccountResult =
  | { ok: true; receipt: DeletionReceipt }
  | { ok: false; reason: 'unauthenticated' }
  /** An emergency cascade is running — deletion is refused until it resolves. */
  | { ok: false; reason: 'emergency' }
  /** Stripe refused; nothing was deleted. */
  | { ok: false; reason: 'billing'; detail?: string }
  | { ok: false; reason: 'server'; detail?: string }
  | { ok: false; reason: 'network'; detail?: string };

export async function deleteAccount(): Promise<DeleteAccountResult> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, reason: 'unauthenticated' };

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/account/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      // Deliberately empty. The server ignores the body and resolves the
      // caregiver from the JWT — see server/routes/account.ts.
      body: JSON.stringify({}),
    });
  } catch (e) {
    return { ok: false, reason: 'network', detail: e instanceof Error ? e.message : undefined };
  }

  const body = await res.json().catch(() => ({} as Record<string, string>));

  if (res.status === 401) return { ok: false, reason: 'unauthenticated' };
  if (res.status === 409) return { ok: false, reason: 'emergency' };
  if (res.status === 502) return { ok: false, reason: 'billing', detail: body?.detail };
  if (!res.ok) return { ok: false, reason: 'server', detail: body?.detail || body?.error };

  return { ok: true, receipt: body as unknown as DeletionReceipt };
}

/** i18n key for a failure, so the caller never has to build the message. */
export function deleteAccountErrorKey(r: Extract<DeleteAccountResult, { ok: false }>): string {
  switch (r.reason) {
    case 'unauthenticated': return 'deleteAccount.errors.signedOut';
    case 'emergency': return 'deleteAccount.errors.emergency';
    case 'billing': return 'deleteAccount.errors.billing';
    case 'network': return 'deleteAccount.errors.network';
    default: return 'deleteAccount.errors.server';
  }
}
